import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Mic, Square, Clock, CheckCircle2, AlertCircle, Loader2, Play, ScanFace, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useV2Proctoring } from './useV2Proctoring';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2InterviewRoom() {
  const { token } = useParams();

  // Interview state — all from server
  const [interview, setInterview] = useState(null);
  const [plan, setPlan] = useState([]);
  const [settings, setSettings] = useState({});
  const [currentSeq, setCurrentSeq] = useState(0);
  const [status, setStatus] = useState('loading'); // loading, ready, reading, answering, saving, completed, error
  const [deadline, setDeadline] = useState(null);
  const [error, setError] = useState(null);

  // Timer
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);

  // Recording
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);

  // Submission lock
  const submittingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  // Track answered sequences to prevent re-answering
  const answeredSeqsRef = useRef(new Set());

  const currentQuestion = plan[currentSeq - 1] || null;
  const [uploadNote, setUploadNote] = useState('');
  const proctorActive = cameraReady && ['reading', 'answering', 'saving', 'ready'].includes(status) && currentSeq > 0;
  const { modelsLoaded, live, takeDelta } = useV2Proctoring({
    videoRef, streamRef: mediaStreamRef, token, active: proctorActive, currentInstanceId: currentQuestion?.question_instance_id,
  });

  // ── Load interview plan (immutable, from server) ──
  useEffect(() => {
    const loadPlan = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/v2/interviews/${token}/plan`);
        const data = res.data;
        setInterview(data);
        setPlan(data.questions || []);
        setSettings(data.settings || {});
        setDeadline(data.interview_deadline ? new Date(data.interview_deadline) : null);

        // Restore answered sequences
        const answered = new Set();
        if (data.questions) {
          data.questions.forEach(q => {
            if (q.status === 'COMPLETED') answered.add(q.sequence_number);
          });
        }
        answeredSeqsRef.current = answered;

        // Determine resume state
        if (!['created', 'in_progress'].includes(data.status)) {
          setStatus('completed');
          setCurrentSeq(data.questions?.length || 0);
        } else if (data.status === 'in_progress') {
          // Resume: find first unanswered
          const nextUnanswered = data.questions?.find(q => q.status !== 'COMPLETED');
          setCurrentSeq(nextUnanswered ? nextUnanswered.sequence_number : data.current_sequence || 1);
          setStatus('ready');
        } else {
          setCurrentSeq(0);
          setStatus('ready');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load interview');
        setStatus('error');
      }
    };
    loadPlan();
  }, [token]);

  // ── Camera setup (acquired once; never restarted mid-interview) ──
  const needCamera = status !== 'loading' && status !== 'error' && status !== 'completed';
  useEffect(() => {
    const setupCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        mediaStreamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraReady(true);
      } catch (err) {
        toast.error('Camera/microphone access required');
      }
    };
    if (needCamera && !mediaStreamRef.current) setupCamera();
    if (!needCamera && mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
  }, [needCamera]);

  useEffect(() => () => { mediaStreamRef.current?.getTracks().forEach(t => t.stop()); }, []);

  // ── Timer ──
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (timeLeft <= 0 || status === 'completed' || status === 'saving') return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Timer expired
          if (status === 'reading') {
            startAnswering();
          } else if (status === 'answering') {
            handleSubmitAnswer();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timeLeft, status]);

  // ── Check hard deadline ──
  useEffect(() => {
    if (!deadline) return;
    const check = setInterval(() => {
      if (new Date() >= deadline && status !== 'completed') {
        clearInterval(check);
        handleTimeLimit();
      }
    }, 1000);
    return () => clearInterval(check);
  }, [deadline, status]);

  // ── Start interview ──
  const handleStart = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/v2/interviews/${token}/start`);
      setDeadline(new Date(res.data.interview_deadline));
      setCurrentSeq(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    }
  };

  // ── Reading phase ──
  const startReading = useCallback(() => {
    const readTime = currentQuestion?.reading_time || settings.reading_time_per_question || 60;
    setTimeLeft(readTime);
    setStatus('reading');
  }, [currentQuestion, settings]);

  // Start reading when currentSeq changes (and we're in ready/reading state)
  useEffect(() => {
    if (currentSeq > 0 && plan.length > 0 && status === 'ready' && !answeredSeqsRef.current.has(currentSeq)) {
      startReading();
    }
  }, [currentSeq, plan.length, status]);

  // ── Start answering ──
  const startAnswering = useCallback(() => {
    const ansTime = currentQuestion?.answering_time || settings.answering_time_per_question || 90;
    setTimeLeft(ansTime);
    setStatus('answering');

    // Start recording
    if (mediaStreamRef.current) {
      chunksRef.current = [];
      try {
        const mr = new MediaRecorder(mediaStreamRef.current, { mimeType: 'video/webm;codecs=vp8,opus' });
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        mr.start(1000);
        mediaRecorderRef.current = mr;
        setIsRecording(true);
      } catch (err) {
        console.error('Recording failed:', err);
      }
    }
  }, [currentQuestion, settings]);

  // ── Submit answer ──
  const handleSubmitAnswer = useCallback(async () => {
    if (submittingRef.current || !currentQuestion) return;
    submittingRef.current = true;
    setSaving(true);
    setStatus('saving');

    // Stop recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    // Wait for final chunks
    await new Promise(r => setTimeout(r, 500));

    // Capture immutable IDs before any state change
    const instanceId = currentQuestion.question_instance_id;
    const seq = currentQuestion.sequence_number;

    try {
      // Step 1: Upload video if we have recorded data
      let videoUrl = '';
      if (chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const reader = new FileReader();
        const base64Data = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });

        for (let attempt = 1; attempt <= 3 && !videoUrl; attempt++) {
          try {
            setUploadNote(attempt > 1 ? `Retrying upload (attempt ${attempt}/3)...` : 'Uploading recording...');
            const uploadRes = await axios.post(`${API_URL}/api/v2/interviews/${token}/upload-video`, {
              video_data: base64Data,
              question_instance_id: instanceId,
              filename: `V2_Q${seq}_${Date.now()}.webm`,
            });
            if (uploadRes.data.success) videoUrl = uploadRes.data.embed_url || '';
          } catch (uploadErr) {
            console.error(`Video upload attempt ${attempt} failed:`, uploadErr);
            if (attempt < 3) await new Promise(r => setTimeout(r, 1500 * attempt));
          }
        }
        if (!videoUrl) toast.error(`Recording for question ${seq} could not be uploaded. It will be flagged as missing evidence.`);
        setUploadNote('');
        chunksRef.current = [];
      }

      // Step 2: Submit answer with question_instance_id
      await axios.post(`${API_URL}/api/v2/interviews/${token}/submit-answer`, {
        question_instance_id: instanceId,
        transcript: '',
        video_url: videoUrl,
        duration: (currentQuestion.answering_time || 90) - timeLeft,
        proctoring_snapshot: takeDelta()
      });

      answeredSeqsRef.current.add(seq);

      // Step 3: Advance
      const nextSeq = seq + 1;
      if (nextSeq <= plan.length) {
        setCurrentSeq(nextSeq);
        setStatus('ready');
      } else {
        await axios.post(`${API_URL}/api/v2/interviews/${token}/complete`, {
          termination_reason: 'COMPLETED',
          proctoring_summary: {}
        });
        setStatus('completed');
        toast.success('Interview completed!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save answer');
      setStatus('answering');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }, [currentQuestion, plan.length, token, timeLeft, takeDelta]);

  // ── Time limit ──
  const handleTimeLimit = async () => {
    try {
      await axios.post(`${API_URL}/api/v2/interviews/${token}/complete`, {
        termination_reason: 'TIME_LIMIT',
        proctoring_summary: takeDelta()
      });
    } catch (err) { console.error(err); }
    setStatus('completed');
    toast.warning('Interview time limit reached');
  };

  // ── Format timer ──
  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // ── Error state ──
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Interview Unavailable</h2>
          <p className="text-muted-foreground">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  // ── Completed state ──
  if (status === 'completed') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Interview Complete</h2>
          <p className="text-muted-foreground mb-4">
            You answered {answeredSeqsRef.current.size} of {plan.length} questions.
          </p>
          <Badge variant="outline" className="text-sm">V2 Engine</Badge>
        </CardContent></Card>
      </div>
    );
  }

  // ── Loading ──
  if (status === 'loading' || !interview) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="v2-interview-room">
      {/* Top Bar */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge className="bg-lime-500/20 text-lime-400 border-lime-500/40">V2 Engine</Badge>
          <span className="text-sm text-slate-400" data-testid="v2-question-progress">
            Question {currentSeq} of {plan.length}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {/* Progress bar */}
          <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-lime-500 transition-all" style={{ width: `${(answeredSeqsRef.current.size / plan.length) * 100}%` }} />
          </div>
          {/* Timer */}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${status === 'answering' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
            <Clock className="w-4 h-4" />
            <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
          </div>
          {/* Recording indicator */}
          {isRecording && (
            <div className="flex items-center gap-1.5 text-red-400">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-medium">REC</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Video Feed */}
        <div className="lg:col-span-1">
          <div className="aspect-[3/4] bg-slate-900 rounded-xl overflow-hidden border border-slate-800 relative">
            <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            <div className="absolute bottom-2 left-2 right-2 flex gap-1.5 flex-wrap" data-testid="v2-proctoring-status">
              <Badge className={`text-[10px] ${modelsLoaded ? 'bg-slate-900/80 text-slate-200' : 'bg-amber-500/30 text-amber-200'}`}>
                <ScanFace className="w-3 h-3 mr-1" />{!modelsLoaded ? 'Proctoring loading' : live.faces === null ? 'Proctoring ready' : live.faces === 0 ? 'No face' : live.faces > 1 ? 'Multiple faces' : 'Face OK'}
              </Badge>
              {live.eyeContact !== null && (
                <Badge className="text-[10px] bg-slate-900/80 text-slate-200"><Eye className="w-3 h-3 mr-1" />{live.eyeContact ? 'Eye contact' : 'Look at camera'}</Badge>
              )}
            </div>
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
              </div>
            )}
          </div>
        </div>

        {/* Question + Controls */}
        <div className="lg:col-span-2 space-y-4">
          {/* Pre-start screen */}
          {currentSeq === 0 && (
            <Card className="bg-slate-900 border-slate-800 text-slate-100">
              <CardContent className="p-8 text-center">
                <h2 className="text-xl font-bold mb-2">V2 Interview Ready</h2>
                <p className="text-slate-400 mb-1">{plan.length} questions • {settings.reading_time_per_question}s reading • {settings.answering_time_per_question}s answering</p>
                <p className="text-slate-500 text-sm mb-6">Maximum duration: {settings.max_interview_duration_minutes} minutes</p>
                <Button onClick={handleStart} size="lg" className="bg-lime-500 hover:bg-lime-600 text-black font-bold" disabled={!cameraReady} data-testid="v2-start-interview">
                  <Play className="w-5 h-5 mr-2" /> Start Interview
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Active question */}
          {currentSeq > 0 && currentQuestion && (
            <>
              <Card className="bg-slate-900 border-slate-800 text-slate-100">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-xs border-slate-700 text-slate-300">{currentQuestion.category_name || 'General'}</Badge>
                    <Badge variant="outline" className="text-xs border-slate-700 text-slate-300">Q{currentSeq}/{plan.length}</Badge>
                  </div>
                  <h2 className="text-lg font-semibold leading-relaxed" data-testid="v2-question-text">
                    {currentQuestion.question_text_snapshot}
                  </h2>
                </CardContent>
              </Card>

              {/* Status + Controls */}
              <Card className="bg-slate-900 border-slate-800 text-slate-100">
                <CardContent className="p-6">
                  {status === 'reading' && (
                    <div className="text-center">
                      <p className="text-blue-400 font-medium mb-3">Reading Time</p>
                      <p className="text-slate-400 text-sm mb-4">Read the question carefully. You can start answering early.</p>
                      <Button onClick={startAnswering} className="bg-lime-500 hover:bg-lime-600 text-black font-bold" data-testid="v2-start-answering">
                        <Mic className="w-4 h-4 mr-2" /> Start Answering
                      </Button>
                    </div>
                  )}

                  {status === 'answering' && (
                    <div className="text-center">
                      <p className="text-red-400 font-medium mb-3 flex items-center justify-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Recording Your Answer
                      </p>
                      <p className="text-slate-400 text-sm mb-4">Speak clearly. Submit when ready or wait for the timer.</p>
                      <Button onClick={handleSubmitAnswer} className="bg-lime-500 hover:bg-lime-600 text-black font-bold" disabled={saving} data-testid="v2-submit-answer">
                        <Square className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Submit Answer'}
                      </Button>
                    </div>
                  )}

                  {status === 'saving' && (
                    <div className="text-center py-4">
                      <Loader2 className="w-8 h-8 animate-spin text-lime-500 mx-auto mb-3" />
                      <p className="text-slate-400" data-testid="v2-saving-status">{uploadNote || 'Saving your answer...'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
